import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Dashboard as DashboardIcon,
  AdminPanelSettings,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AsuretiLogo from './AsuretiLogo';

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 0);
  };

  const handleDashboard = () => {
    handleMenuClose();
    setTimeout(() => {
      navigate('/dashboard');
    }, 0);
  };

  const handleAdmin = () => {
    handleAdminMenuClose();
    setTimeout(() => {
      navigate('/admin');
    }, 0);
  };

  const handleCustomFields = () => {
    handleAdminMenuClose();
    // Use setTimeout to ensure menu closes before navigation
    setTimeout(() => {
      navigate('/admin/custom-fields');
    }, 0);
  };

  const handleUserAdmin = () => {
    handleAdminMenuClose();
    setTimeout(() => {
      navigate('/admin/users');
    }, 0);
  };

  const handleAdminObligations = () => {
    handleAdminMenuClose();
    setTimeout(() => {
      navigate('/admin/obligations');
    }, 0);
  };

  const handleObligations = () => {
    handleMenuClose();
    setTimeout(() => {
      navigate('/obligations');
    }, 0);
  };

  const handleProfile = () => {
    handleMenuClose();
    setTimeout(() => {
      navigate('/profile');
    }, 0);
  };

  return (
    <AppBar position="static" sx={{ mb: 3 }}>
      <Toolbar>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            cursor: 'pointer'
          }}
          onClick={() => navigate('/dashboard')}
        >
          <AsuretiLogo 
            variant="icon-white" 
            width={32} 
            height={40} 
            sx={{ mr: 2 }} 
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              color: 'white'
            }}
          >
            Compliance Tracker
          </Typography>
        </Box>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Dashboard Button */}
            <Button
              color="inherit"
              startIcon={<DashboardIcon />}
              onClick={handleDashboard}
            >
              Dashboard
            </Button>

            {/* Obligations Button */}
            <Button
              color="inherit"
              startIcon={<AssignmentIcon />}
              onClick={handleObligations}
            >
              Obligations
            </Button>

            {/* Admin Button - Only show for admin users */}
            {user.role === 'ADMIN' && (
              <>
                <Button
                  color="inherit"
                  startIcon={<AdminPanelSettings />}
                  onClick={handleAdminMenuOpen}
                  aria-controls="admin-menu"
                  aria-haspopup="true"
                >
                  Admin
                </Button>
                <Menu
                  id="admin-menu"
                  anchorEl={adminMenuAnchorEl}
                  open={Boolean(adminMenuAnchorEl)}
                  onClose={handleAdminMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  disableAutoFocusItem
                  disableRestoreFocus
                  slotProps={{
                    paper: {
                      'aria-hidden': undefined,
                    },
                  }}
                >
                  <MenuItem onClick={handleAdmin}>
                    <DashboardIcon sx={{ mr: 2 }} />
                    Admin Dashboard
                  </MenuItem>
                  <MenuItem onClick={handleUserAdmin}>
                    <GroupsIcon sx={{ mr: 2 }} />
                    User Administration
                  </MenuItem>
                  <MenuItem onClick={handleAdminObligations}>
                    <AssignmentIcon sx={{ mr: 2 }} />
                    Manage Obligations
                  </MenuItem>
                  <MenuItem onClick={handleCustomFields}>
                    <SettingsIcon sx={{ mr: 2 }} />
                    Custom Fields
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* User Menu */}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user.firstName[0]}{user.lastName[0]}
              </Avatar>
            </IconButton>

            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              disableAutoFocusItem
              disableRestoreFocus
              slotProps={{
                paper: {
                  'aria-hidden': undefined,
                },
              }}
            >
              <MenuItem onClick={handleProfile}>
                <AccountCircle sx={{ mr: 2 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 2 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;
